import { useEffect, useRef, useState } from 'react';
import { Download } from 'lucide-react';
import {
  type FingerprintSnapshot,
  downloadBlob,
  fingerprintFileName,
  fingerprintToJson,
  fingerprintToMarkdown,
} from '@/lib/exportFingerprint';

/**
 * "导出 ▾" dropdown shown in the device-overview header. Two formats:
 *
 *   - JSON  → exact dashboard data, suitable for diffing across days /
 *             feeding into automation pipelines.
 *   - Markdown → human-friendly summary that pastes cleanly into Notion /
 *             Lark / GitHub. Each group becomes an `##` section, each card
 *             a `###` block.
 *
 * The actual serializers live in `lib/exportFingerprint.ts` so they can be
 * unit-tested without rendering this component.
 */
export function ExportDeviceFingerprint({
  serial,
  user,
  buildSnapshot,
  disabled,
}: {
  serial: string;
  user?: string;
  /**
   * Lazy snapshot factory: invoked when the user picks a format. We don't
   * keep a cached snapshot in state because the dashboard's react-query
   * cache may have refreshed between renders.
   */
  buildSnapshot: () => FingerprintSnapshot;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Click-away closes the menu so it behaves like a native <select>.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onExport = (fmt: 'json' | 'md') => {
    setOpen(false);
    try {
      const snap = buildSnapshot();
      if (!snap.user && user) snap.user = user;
      const body = fmt === 'json' ? fingerprintToJson(snap) : fingerprintToMarkdown(snap);
      const mime = fmt === 'json' ? 'application/json' : 'text/markdown';
      downloadBlob(fingerprintFileName(serial, fmt), mime, body);
    } catch (err) {
      // Surface the failure visibly — silent failure on a "save my data"
      // button is the worst UX outcome possible.
      console.error('export fingerprint failed', err);
      // eslint-disable-next-line no-alert
      alert(`导出失败：${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="btn"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        title="导出当前设备总览快照"
      >
        <Download className="h-4 w-4" />
        <span>导出</span>
        <span className="text-[10px] opacity-60">▾</span>
      </button>
      {open && (
        <div
          className="absolute right-0 z-20 mt-1 min-w-[180px] overflow-hidden rounded border border-line bg-bg-card shadow-lg"
          role="menu"
        >
          <button
            type="button"
            className="block w-full px-3 py-2 text-left text-xs hover:bg-bg-muted"
            onClick={() => onExport('json')}
            role="menuitem"
          >
            <div className="font-medium">JSON</div>
            <div className="text-[10px] text-ink-dim">完整结构化快照（机器可读）</div>
          </button>
          <button
            type="button"
            className="block w-full border-t border-line/60 px-3 py-2 text-left text-xs hover:bg-bg-muted"
            onClick={() => onExport('md')}
            role="menuitem"
          >
            <div className="font-medium">Markdown</div>
            <div className="text-[10px] text-ink-dim">分组摘要 · 适合粘贴到文档</div>
          </button>
        </div>
      )}
    </div>
  );
}
